import React, { useEffect } from "react";
import { TextField, Switch, Box, Grid, Backdrop, Checkbox, Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string } from "yup";
import {
  addNewInventoryItemStatus,
  updateInventoryItemStatus,
  getAllInventoryItemStatuses,
} from "src/app-redux/configuration/actions/configurationActions";
import {
  selectAllInventoryItemStatusesList,
  loader,
} from "src/app-redux/configuration/configurationSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import { IAddInventoryItemStatus } from "src/common/typings/configuration";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import { columns } from "./columns";
import { ColorPicker } from "material-ui-color";
import { Prompt } from "react-router-dom";
import { colorCodesInventory } from "src/common/utils/app.dropdowns";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import CommonLoader from "src/components/Loader/CommonLoader";

const useStyles = makeStyles((theme: Theme) => ({
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
    paddingRight: 0,
  },
  mainConainer: { marginTop: "0px" },
}));

export interface IForm extends IAddInventoryItemStatus {}

interface Props {}

export function InputFields({}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [isEditMode, setisEditMode] = React.useState<boolean>(false);
  const loading = useAppSelector(loader);
  const allInventoryItemStatusRows = useAppSelector(selectAllInventoryItemStatusesList);

  // Form Submission
  const validationSchema = object({
    status: string()
      .required("This field is required")
      .max(50, "Maximum 50 characters are allowed."),
    colorCode: string()
      .required("This field is required")
      .matches(
        new RegExp("^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"),
        "Only hex color codes are allowed"
      ),
  });
  const formik = useFormik<IAddInventoryItemStatus>({
    enableReinitialize: true,
    initialValues: {
      id: "",
      status: "",
      colorCode: "",
      isOutOfWarehouse: false,
      isActive: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      let res;
      if (isEditMode) res = await dispatch(updateInventoryItemStatus(values));
      else res = await dispatch(addNewInventoryItemStatus(values));
      if (res?.payload) {
        dispatch(getAllInventoryItemStatuses());
        actions.setSubmitting(false);
        actions.resetForm();
        setisEditMode(false);
        setIsDirty(false);
      }
    },
  });
  // End Form Submission
  const handleUpdateClick = (params: any) => {
    setisEditMode(true);
    formik.setFieldValue("id", params.id);
    formik.setFieldValue("status", params.status);
    formik.setFieldValue("colorCode", params.colorCode);
    formik.setFieldValue("isOutOfWarehouse", params.isOutOfWarehouse);
    formik.setFieldValue("isActive", params.isActive);
  };

  useEffect(() => {
    dispatch(getAllInventoryItemStatuses());
  }, []);

  /* Transform Column */
  useEffect(() => {
    if (allInventoryItemStatusRows.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "colorCode"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                      className="colorPicker"
                      style={{
                        position: "relative",
                        zIndex: 0,
                        width: "20px",
                        height: "20px",
                        margin: "10px 10px 14px 0",
                        borderRadius: "3px",
                        backgroundColor: row.original.colorCode,
                      }}
                    ></div>
                    {colorCodesInventory.find((st: any) => st.code === row.original.colorCode)
                      ?.color || "N/A"}
                  </div>
                );
              },
            }
          : column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        handleUpdateClick(row.original);
                        setIsDirty(true);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column.accessorKey === "isOutOfWarehouse"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isOutOfWarehouse ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isOutOfWarehouse ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [allInventoryItemStatusRows]);

  // Show prompt if the form is fully or partially filled and user wants to navigate away
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    // If prompt, when reloading or closing the tab
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);
  function handleChange(e: any) {
    formik.handleChange(e);
    e.target.value ? setIsDirty(true) : setIsDirty(false);
  }

  return (
    <>
      {/* If prompt, when navigating to another page inside the app */}
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          {/* First Section */}
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                // Container
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="flex-start"
                spacing={2}
              >
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={12} md={6} lg={4} style={{ paddingLeft: "0px", paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        type="string"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Item Status"
                        placeholder="Enter Item Status"
                        name="status"
                        disabled={false}
                        value={formik.values.status}
                        onChange={handleChange}
                        error={formik.touched.status && Boolean(formik.errors.status)}
                        helperText={formik.touched.status && formik.errors.status}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6} lg={3} style={{ paddingLeft: "0px", paddingRight: 0 }}>
                    <Box sx={{ marginLeft: { xs: 0, md: "8px" } }}>
                      <div
                        className="colorPicker"
                        style={{
                          position: "relative",
                          zIndex: 0,
                          width: "20px",
                          height: "20px",
                          margin: "9px",
                          marginBottom: "-29px",
                          borderRadius: "3px",
                          backgroundColor: formik.values.colorCode,
                        }}
                      ></div>
                      <div className="autoCompleteField-colorCode">
                        <CommonAutoCompleteField
                          cssClassName="customTextField"
                          disabled={false}
                          label="Color"
                          placeholder="Enter Color"
                          name="colorCode"
                          optionsType={colorCodesInventory}
                          getOptionLabel={(option) => option.color}
                          renderOption={(option) => (
                            <>
                              <div
                                className="colorPicker"
                                style={{
                                  position: "relative",
                                  marginBottom: "0",
                                  zIndex: 1,
                                }}
                              >
                                <ColorPicker
                                  value={option.code}
                                  hideTextfield
                                  onChange={() => {
                                    //do nothing
                                  }}
                                />
                              </div>
                              <span>{option.color}</span>
                            </>
                          )}
                          required
                          value={
                            colorCodesInventory.find(
                              (st: any) => st.code === formik.values.colorCode
                            ) || null
                          }
                          onChange={(option: any) => {
                            option && option !== ""
                              ? formik.setFieldValue("colorCode", option.code)
                              : "";
                          }}
                          error={formik.touched.colorCode && Boolean(formik.errors.colorCode)}
                          helperText={formik.touched.colorCode && formik.errors.colorCode}
                        />
                      </div>
                    </Box>
                  </Grid>
                  <Grid item xs={7} md={5} lg={2} style={{ paddingLeft: "0px", paddingRight: 0 }}>
                    <Box
                      sx={{
                        textAlign: { xs: "left", lg: "center" },
                        marginLeft: { xs: 0, lg: "8px" },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(formik.values.isOutOfWarehouse)}
                            onChange={formik.handleChange}
                            name="isOutOfWarehouse"
                            color="primary"
                          />
                        }
                        label="Out of Warehouse"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={5} md={5} lg={2} style={{ paddingLeft: "0px", paddingRight: 0 }}>
                    <Box sx={{ marginLeft: { xs: 0, lg: "8px" } }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formik.values.isActive)}
                            onChange={formik.handleChange}
                            name="isActive"
                            color="primary"
                          />
                        }
                        label="Active"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={2} lg={1} style={{ paddingRight: 0 }}>
                    <Box mb={1} mt={1} className={"headerButtonsConfig"} style={{ float: "right" }}>
                      <CommonButton
                        isGreen
                        cssClass="customSubmitBtn"
                        onClick={() => {
                          formik.submitForm();
                        }}
                      >
                        {isEditMode ? "Update" : "Add"}
                      </CommonButton>
                    </Box>
                  </Grid>
                  <Grid md={12}>
                    <CommonMaterialGrid
                      defaultPerPageRecord={10}
                      height={400}
                      rows={allInventoryItemStatusRows || []}
                      columns={displayColumns}
                    />
                  </Grid>
                </Grid>
                {/* End Inner Container */}
              </Grid>
              {/* End Outer Container */}
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
